import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "motion/react";
import { auth, db, isRealFirebaseEnabled, signInWithGoogle, logoutFromGoogle, incrementPageView, checkAndHandleQuotaExceeded } from "./lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, doc, setDoc, getDoc, getDocs, query, limit } from "firebase/firestore";
import { staticTools } from "./data/staticTools";
import { 
  Heart, 
  Coffee, 
  Star, 
  Smartphone, 
  ArrowRight, 
  CheckCircle2, 
  Camera, 
  Fingerprint, 
  Users, 
  Award, 
  ShieldCheck, 
  ChevronLeft, 
  ChevronRight, 
  X,
  Play,
  Github,
  Twitter,
  Instagram,
  Youtube,
  Globe,
  Sparkles,
  Search,
  BookOpen,
  UserCheck,
  Leaf,
  ImagePlus,
  Database,
  Brain
} from "lucide-react";
import { UserState } from "./types";
import Header from "./components/Header";
import Scanner from "./components/Scanner";
import TableOfContents from "./components/TableOfContents";
import ScrollProgress from "./components/ScrollProgress";
import Footer from "./components/Footer";
import SignInModal from "./components/SignInModal";
import { ProfessionalLoader, AdminProfessionalLoader, RouteProgressBar } from "./components/PageLoader";

// Lazy-loaded subpage routes and below-the-fold homepage components
const Dashboard = lazy(() => import("./components/Dashboard"));
const IdentifyPage = lazy(() => import("./components/IdentifyPage"));
const PlantDetailsPage = lazy(() => import("./components/PlantDetailsPage"));
const DatabasePage = lazy(() => import("./components/DatabasePage"));
const BlogArticlePage = lazy(() => import("./components/BlogArticlePage"));
const AdminPage = lazy(() => import("./components/AdminPage"));
const StaticPage = lazy(() => import("./components/StaticPage"));
const AccountDeletePage = lazy(() => import("./components/AccountDeletePage"));
const ToolPage = lazy(() => import("./components/ToolPage"));

// Directly imported homepage components for complete, immediate rendering and smooth scrolling
import LegalHub from "./components/LegalHub";
import BentoGrid from "./components/BentoGrid";
import ToolsShowcase from "./components/ToolsShowcase";
import HowItWorks from "./components/HowItWorks";
import TrustStats from "./components/TrustStats";
import PlantCategories from "./components/PlantCategories";
import PlantLearningCenter from "./components/PlantLearningCenter";
import AboutTheProject from "./components/AboutTheProject";
import PlantDatabase from "./components/PlantDatabase";
import FAQ from "./components/FAQ";
import BlogSection from "./components/BlogSection";
import AccountBenefits from "./components/AccountBenefits";
import HomepagePillars, { HomepageTargetedKeywordsBox } from "./components/HomepagePillars";

const LazyFallback = () => <ProfessionalLoader />;
const AdminLazyFallback = () => <AdminProfessionalLoader />;

export default function App() {
  const [route, setRoute] = useState<string>(window.location.pathname);
  const [isNavigating, setIsNavigating] = useState(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setIsNavigating(true);
    const timer = setTimeout(() => {
      setIsNavigating(false);
    }, 450);
    return () => clearTimeout(timer);
  }, [route]);

  const [user, setUser] = useState<UserState | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isSignInOpen, setIsSignInOpen] = useState(false);
  const [isGetAppOpen, setIsGetAppOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [dashboardTab, setDashboardTab] = useState<any>("overview");
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [coffeeCount, setCoffeeCount] = useState(0);
  const [activeTrustVariant, setActiveTrustVariant] = useState<"A" | "B" | "C" | "D">("A");
  const [toolsList, setToolsList] = useState<any[]>(staticTools);

  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminRoleName, setAdminRoleName] = useState<string>("");
  const [adminInvitation, setAdminInvitation] = useState<any | null>(null);

  // Dynamic Google Analytics 4 Loader with Firestore configuration sync
  useEffect(() => {
    if (!isRealFirebaseEnabled || !db) {
      // Offline fallback
      injectGtag("G-X9HCF0BL2J");
      return;
    }

    async function loadGa4Config() {
      try {
        const snap = await getDoc(doc(db!, "site_settings", "global"));
        let measurementId = "G-X9HCF0BL2J"; // default fallback
        let isConnected = true;

        if (snap.exists()) {
          const data = snap.data();
          if (data.ga4MeasurementId) {
            measurementId = data.ga4MeasurementId;
          }
          if (typeof data.ga4Connected !== 'undefined') {
            isConnected = data.ga4Connected;
          }
        }

        if (isConnected && measurementId) {
          injectGtag(measurementId);
        } else {
          removeGtag();
        }
      } catch (err) {
        console.warn("Dynamic GA4 configuration fetch failed, using fallback:", err);
        injectGtag("G-X9HCF0BL2J");
      }
    }
    loadGa4Config();

    function injectGtag(id: string) {
      const existingScript = document.getElementById("dynamic-gtag-script") as HTMLScriptElement;
      if (existingScript && existingScript.src.includes(id)) {
        return;
      }

      removeGtag();

      const script = document.createElement("script");
      script.id = "dynamic-gtag-script";
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      document.head.appendChild(script);

      const inlineScript = document.createElement("script");
      inlineScript.id = "dynamic-gtag-config";
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        window.gtag = gtag;
        gtag('config', '${id}');
      `;
      document.head.appendChild(inlineScript);
    }

    function removeGtag() {
      const existingScript = document.getElementById("dynamic-gtag-script");
      if (existingScript) existingScript.remove();
      const existingConfig = document.getElementById("dynamic-gtag-config");
      if (existingConfig) existingConfig.remove();
    }
  }, [isRealFirebaseEnabled]);

  // Dynamically load Google AdSense strictly on user interaction to maximize mobile & desktop PageSpeed scores
  useEffect(() => {
    let loaded = false;
    const loadAdSense = () => {
      if (loaded) return;
      loaded = true;
      
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1012314300176834";
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
      
      // Clean up event listeners
      window.removeEventListener("scroll", loadAdSense);
      window.removeEventListener("touchstart", loadAdSense);
      window.removeEventListener("mousemove", loadAdSense);
    };

    // Load immediately when the user interacts with the page (scrolls, touches, or moves mouse)
    window.addEventListener("scroll", loadAdSense, { passive: true });
    window.addEventListener("touchstart", loadAdSense, { passive: true });
    window.addEventListener("mousemove", loadAdSense, { passive: true });

    return () => {
      window.removeEventListener("scroll", loadAdSense);
      window.removeEventListener("touchstart", loadAdSense);
      window.removeEventListener("mousemove", loadAdSense);
    };
  }, []);

  // Hook up state check for admin role
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setAdminRoleName("");
      setAdminInvitation(null);
      return;
    }

    // Hardcode check for the Super Admin
    if (user.email === "bishalkhanal7021@gmail.com") {
      if (user.uid.startsWith("simulated-") || user.uid.startsWith("mock-") || user.uid.startsWith("email-")) {
        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        if (!isLocalhost) {
          setIsAdmin(false);
          setAdminRoleName("");
          setAdminInvitation(null);
          return;
        }
      }
      setIsAdmin(true);
      setAdminRoleName("Super Admin");
      setAdminInvitation(null);
      return;
    }

    if (!isRealFirebaseEnabled || !db) {
      // In simulation mode, check if email is bishalkhanal7021
      setIsAdmin(false);
      setAdminRoleName("");
      setAdminInvitation(null);
      return;
    }

    // Listen to their role document
    const docRef = doc(db, "admin_roles", user.email);
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.status === "active") {
          setIsAdmin(true);
          setAdminRoleName(data.role || "Admin");
          setAdminInvitation(null);
        } else if (data.status === "invited") {
          setIsAdmin(false);
          setAdminRoleName("");
          setAdminInvitation(data);
        } else {
          setIsAdmin(false);
          setAdminRoleName("");
          setAdminInvitation(null);
        }
      } else {
        setIsAdmin(false);
        setAdminRoleName("");
        setAdminInvitation(null);
      }
    }, (error) => {
      checkAndHandleQuotaExceeded(error);
      console.warn("Error listening to admin roles:", error);
    });

    return () => unsubscribe();
  }, [user]);

  const acceptAdminInvitation = async () => {
    if (!user) return;
    if (!isRealFirebaseEnabled || !db) {
      setIsAdmin(true);
      setAdminRoleName("Admin");
      setAdminInvitation(null);
      return;
    }
    if (!adminInvitation) return;
    try {
      const docRef = doc(db, "admin_roles", user.email);
      await setDoc(docRef, {
        ...adminInvitation,
        status: "active",
        userId: user.uid
      });
    } catch (err) {
      console.error("Failed to accept admin invitation:", err);
    }
  };

  const declineAdminInvitation = async () => {
    if (!user) return;
    if (!isRealFirebaseEnabled || !db) {
      setAdminInvitation(null);
      return;
    }
    try {
      const docRef = doc(db, "admin_roles", user.email);
      await setDoc(docRef, {
        ...adminInvitation,
        status: "declined"
      });
      setAdminInvitation(null);
    } catch (err) {
      console.error("Failed to decline admin invitation:", err);
    }
  };

  const scannerRef = useRef<HTMLDivElement>(null);

  // Sync client route handling on Pop State triggers
  useEffect(() => {
    const handleLocationChange = () => {
      setRoute(window.location.pathname);
    };
    window.addEventListener("popstate", handleLocationChange);
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  // Protect dashboard routes for unauthenticated users
  useEffect(() => {
    const isDbRoute = route.startsWith("/dashboard") || route === "/delete-account" || route === "/account-delete";
    if (!authLoading && !user && isDbRoute) {
      setIsSignInOpen(true);
      navigate("/");
    }
  }, [user, authLoading, route]);

  // Live GA4-compatible visitor page views tracker (deferred to prioritize initial render)
  useEffect(() => {
    const timer = setTimeout(() => {
      incrementPageView().catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Load Tools from Firestore (Deferred to keep main thread free during hydration)
  useEffect(() => {
    if (!isRealFirebaseEnabled || !db) {
      setToolsList(staticTools);
      return;
    }
    const timer = setTimeout(() => {
      async function loadTools() {
        try {
          const snapshot = await getDocs(query(collection(db!, "tools"), limit(50)));
          const fetched: any[] = [];
          snapshot.forEach((doc) => {
            fetched.push({ id: doc.id, ...doc.data() });
          });
          if (fetched.length === 0) {
            setToolsList(staticTools);
          } else {
            // Merge staticTools with Firestore fetched tools so all core tools remain active
            const mergedMap = new Map<string, any>();
            staticTools.forEach(t => {
              if (t.id) mergedMap.set(t.id, t);
              if (t.link) mergedMap.set(t.link, t);
            });
            fetched.forEach(t => {
              if (t.id) mergedMap.set(t.id, t);
              if (t.link) mergedMap.set(t.link, t);
            });
            const mergedList = Array.from(new Set(Array.from(mergedMap.values())));
            setToolsList(mergedList);
          }
        } catch {
          setToolsList(staticTools);
        }
      }
      loadTools();
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  // Dynamically set SEO metadata, titles, and canonical links based on current active route
  useEffect(() => {
    const routeTitles: Record<string, string> = {
      "/": "Plant Identifier – AI Plant Identification & Botanical Database",
      "/account-delete": "Delete Your Account | Plant Identifier Backup Purge",
      "/identify": "Identify Plant | Rapid AI Botanical Scanner",
      "/identify-plant": "Identify Plant | Rapid AI Botanical Scanner",
      "/database": "Plant Database | Browse Botanical Taxonomy & Care Specs",
      "/plant-database": "Plant Database | Browse Botanical Taxonomy & Care Specs",
      "/admin": "Admin Panel | Plant Identifier System Controls",
      "/about": "About Us | Learn About Plant Identifier",
      "/contact": "Contact Us | Certified Horticulturalist Support",
      "/privacy-policy": "Privacy Policy | Plant Identifier Data Safeguards",
      "/terms-of-service": "Terms of Service | Platform Usage Guidelines",
      "/disclaimer": "Botanical & Medical Disclaimer | Plant Identifier Limits",
      "/cookie-policy": "Cookie Policy | Standard Caching Disclosures",
      "/faq": "FAQ | Plant Identification Frequently Asked Questions",
      "/blog": "Botanical Blog | Master Garden Cultivation & Diagnostics",
      "/plant-care-guides": "Plant Care Guides | Master Horticultural Care Sheets",
      "/plant-diseases": "Common Plant Diseases | Diagnostic Symptoms & Remedies",
      "/common-plant-diseases": "Common Plant Diseases | Diagnostic Symptoms & Remedies",
      "/botanical-articles": "Botanical Articles | Scientific Botany & Plant Studies",
      "/plant-care-tips": "Plant Care Tips | Quick Actionable Irrigation & Pruning Advice",
      "/gardening-guides": "Gardening Guides | Container Gardening & Propagation Manual",
      "/data-sources": "Data Sources | Plant Identifier AI Complete Datasets",
      "/editorial-policy": "Editorial Policy | Helpful Content Quality Guidelines",
      "/attribution": "Data Attribution | Trusted Botanical Datasets",
    };

    const routeDescriptions: Record<string, string> = {
      "/": "Identify plants accurately using AI-powered recognition, scientific plant database, botanical articles, plant care guides, and educational resources.",
      "/account-delete": "A secure, verified control deck to permanently delete your PlantID companion profile and delete all associated botanical archives.",
      "/identify": "Upload a picture of your houseplant or garden crop to easily identify species classification and disease markers with deep-learning precision.",
      "/identify-plant": "Upload a picture of your houseplant or garden crop to easily identify species classification and disease markers with deep-learning precision.",
      "/database": "Analyze hundreds of plant classification records detailing family taxonomies, direct sunlight profiles, irrigation frequencies, and soil requirements.",
      "/plant-database": "Analyze hundreds of plant classification records detailing family taxonomies, direct sunlight profiles, irrigation frequencies, and soil requirements.",
      "/admin": "Administrative control deck to manage botanical logs, AI metadata confidence metrics, and community feedback surveys.",
      "/about": "Meet our team of digital imaging experts and certified horticultural advisors. We build highly precise botanical diagnostic companion apps.",
      "/contact": "Reach out directly to our global, certified botanical support team for technical guidance or research API access.",
      "/privacy-policy": "Read how we preserve uploaded garden photos temporarily on sandboxed endpoints, ensuring zero commercial behavioral tracking tags.",
      "/terms-of-service": "Understand the terms of service governing amateur crop diagnostics, scientific reporting, and API lifetime licenses.",
      "/disclaimer": "Important statements illustrating limitations of artificial intelligence taxonomy, toxic flora listings, and pesticide administration risks.",
      "/cookie-policy": "Learn how our lightweight pages utilize functional local cache keys strictly to improve page rendering time without ad-trackers.",
      "/faq": "Find accurate answers regarding camera specifications, confidence weights, plant family metrics, and offline app APK parameters.",
      "/blog": "Dive deep into modern organic farming patterns, soil organic matter maintenance, mycorrhizal systems, and houseplant troubleshooting.",
      "/plant-care-guides": "An extensive manual library covering watering, nitrogen level profiles, relative humidity bounds, and potting cycles.",
      "/plant-diseases": "Identify common plant diseases like Powdery Mildew, Anthracnose, Root Rot, and Spider Mites with organic remedy protocols.",
      "/common-plant-diseases": "Identify common plant diseases like Powdery Mildew, Anthracnose, Root Rot, and Spider Mites with organic remedy protocols.",
      "/botanical-articles": "Read scientific studies exploring photosynthesis efficiency, chlorophyll density meters, and plant vascular systems.",
      "/plant-care-tips": "Simple master tip charts listing rapid irrigation schedules, root aeration, winter leaf dusting, and balanced nitrogen fertilizers.",
      "/gardening-guides": "Step-by-step masterclasses on small-space layouts, soil pH levels, balanced green composting, and companion sowing.",
      "/data-sources": "Discover the open biological and taxonomic datasets powering our accurate flora catalog, including global open biodiversity and herbaria metrics.",
      "/editorial-policy": "Read how we enforce academic integrity, fact-check agricultural reports, and offer direct access to verified organic botanical practices.",
      "/attribution": "A listing of all taxonomic datasets, consensual checklists, and scientific herbaria we reference on our site, with credits to verified open-source checklists.",
    };

    let titleText = routeTitles[route] || "Plant Identifier Hub";
    let descriptionText = routeDescriptions[route] || "Identify plants accurately using AI-powered recognition, scientific plant database, botanical articles, plant care guides, and educational resources.";

    // Load custom SEO controls if set by Dashboard / Organic SEO Tab
    const savedMetaTitle = localStorage.getItem("seo_meta_title");
    const savedMetaDesc = localStorage.getItem("seo_meta_desc");
    const savedCanonicalBase = localStorage.getItem("seo_canonical_url") || "https://plantidentifer.com";
    const savedOgTitle = localStorage.getItem("seo_og_title") || "Plant Identifier";
    const savedOgDesc = localStorage.getItem("seo_og_description") || "AI-powered plant identification and botanical knowledge platform.";

    if (route === "/") {
      if (savedMetaTitle) titleText = savedMetaTitle;
      if (savedMetaDesc) descriptionText = savedMetaDesc;
    }

    // Handle dynamic plant and blog page titles
    if (route.startsWith("/plant/")) {
      const plantSlug = route.substring("/plant/".length);
      const cleanName = plantSlug.split("-").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
      titleText = `${cleanName} - Care Profile & Database | Plant Identifier`;
      descriptionText = `Master professional care for ${cleanName}. Discover ideal sunlight bounds, toxic indicators, irrigation levels, and pruning techniques.`;
    } else if (route.startsWith("/blog/")) {
      const blogSlug = route.substring("/blog/".length);
      const cleanTitle = blogSlug.split("-").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
      titleText = `${cleanTitle} - Botanical Study | Plant Identifier Blog`;
      descriptionText = `Dive into the scientific research: ${cleanTitle}. Read certified agronomy analysis and step-by-step gardening guides.`;
    } else if (route.startsWith("/botanical-articles/")) {
      const artSlug = route.substring("/botanical-articles/".length);
      const cleanTitle = artSlug.split("-").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
      titleText = `${cleanTitle} - Scientific Study | Botany Library`;
      descriptionText = `Read our scientific botanical review: ${cleanTitle}. Clear, organic insight directly on our plant knowledge base.`;
    } else if (route.startsWith("/plants/")) {
      const catSlug = route.substring("/plants/".length);
      const cleanName = catSlug.split("-").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
      titleText = `${cleanName} Portfolio | Live Plant Database & Care Guides`;
      descriptionText = `Master professional horticulture care for ${cleanName} flora. Browse lists, species details, ideal sunlight parameters, and watering cycles.`;
    }

    document.title = titleText;

    // Manage SEO description meta
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", descriptionText);

    // Manage SEO canonical link directly forcing singular canonical base https://plantidentifer.com
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", `${savedCanonicalBase.replace(/\/$/, "")}${route}`);

    // Manage Open Graph & Twitter Card tags
    const getMetaTag = (property: string, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, property);
        document.head.appendChild(el);
      }
      return el;
    };

    getMetaTag("og:site_name").setAttribute("content", "Plant Identifier");
    getMetaTag("og:title").setAttribute("content", route === "/" ? savedOgTitle : titleText);
    getMetaTag("og:description").setAttribute("content", route === "/" ? savedOgDesc : descriptionText);
    getMetaTag("og:type").setAttribute("content", route === "/" ? "website" : "article");
    getMetaTag("og:url").setAttribute("content", `${savedCanonicalBase.replace(/\/$/, "")}${route}`);
    getMetaTag("og:image").setAttribute("content", "https://plantidentifer.com/assets/logo.png");

    getMetaTag("twitter:card", true).setAttribute("content", "summary_large_image");
    getMetaTag("twitter:title", true).setAttribute("content", route === "/" ? savedOgTitle : titleText);
    getMetaTag("twitter:description", true).setAttribute("content", route === "/" ? savedOgDesc : descriptionText);

    // Structured Data Schemas
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Plant Identifier",
      "url": "https://plantidentifer.com",
      "logo": "https://plantidentifer.com/assets/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "contact@plantidentifer.com",
        "contactType": "customer support"
      }
    };

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Plant Identifier",
      "url": "https://plantidentifer.com",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://plantidentifer.com/database?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };

    const pathSegments = route.split("/").filter(Boolean);
    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://plantidentifer.com"
        },
        ...pathSegments.map((segment, idx) => {
          const path = "/" + pathSegments.slice(0, idx + 1).join("/");
          const name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
          return {
            "@type": "ListItem",
            "position": idx + 2,
            "name": name,
            "item": `https://plantidentifer.com${path}`
          };
        })
      ]
    };

    let schemaScript = document.getElementById("structured-data-schema");
    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.setAttribute("id", "structured-data-schema");
      schemaScript.setAttribute("type", "application/ld+json");
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify([orgSchema, websiteSchema, breadcrumbList]);

  }, [route]);

  // Soft auto-cycling for the trust ribbon options to highlight different portal strengths
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTrustVariant((current) => {
        if (current === "A") return "B";
        if (current === "B") return "C";
        if (current === "C") return "D";
        return "A";
      });
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const navigate = (path: string, options?: { scroll?: boolean }) => {
    window.history.pushState(null, "", path);
    setRoute(path);
    if (options?.scroll !== false) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getTabFromRoute = (currentRoute: string): any => {
    if (currentRoute === "/dashboard" || currentRoute === "/dashboard/overview") return "overview";
    if (currentRoute === "/dashboard/collection") return "collection";
    if (currentRoute === "/dashboard/recent-scans" || currentRoute === "/dashboard/history") return "history";
    if (currentRoute === "/dashboard/favorites") return "favorites";
    if (currentRoute === "/dashboard/my-plant-vault" || currentRoute === "/dashboard/vault") return "vault";
    if (currentRoute === "/dashboard/my-reviews" || currentRoute === "/dashboard/reviews") return "reviews";
    if (currentRoute === "/dashboard/account-settings" || currentRoute === "/dashboard/settings") return "settings";
    if (currentRoute === "/delete-account" || currentRoute === "/account-delete" || currentRoute === "/dashboard/delete-account") return "delete-account";
    return "overview";
  };

  const handleDashboardTabClick = (tab?: string) => {
    const tabName = tab || "overview";
    const pathMap: Record<string, string> = {
      overview: "/dashboard",
      collection: "/dashboard/collection",
      history: "/dashboard/recent-scans",
      favorites: "/dashboard/favorites",
      vault: "/dashboard/my-plant-vault",
      reviews: "/dashboard/my-reviews",
      settings: "/dashboard/account-settings",
      "delete-account": "/delete-account"
    };
    navigate(pathMap[tabName] || "/dashboard");
  };

  // Listen to Auth State changes on real Firebase
  useEffect(() => {
    if (!isRealFirebaseEnabled || !auth) {
      setAuthLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          name: firebaseUser.displayName || "Botanist Explorer",
          email: firebaseUser.email || "",
          photoURL: firebaseUser.photoURL || undefined
        });
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Google Sign In Flow (handles real or simulated)
  const handleSignIn = async () => {
    if (isRealFirebaseEnabled) {
      try {
        await signInWithGoogle();
        setIsSignInOpen(false);
      } catch (err) {
        console.error("Firebase pop-up sign-in failed:", err);
        throw err;
      }
    } else {
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      if (isLocalhost) {
        setUser({
          uid: "simulated-user-123",
          name: "Bishal Khanal (Local Dev)",
          email: "bishalkhanal7021@gmail.com",
          photoURL: ""
        });
      } else {
        setUser({
          uid: "simulated-user-guest",
          name: "Guest Explorer",
          email: "guest@plantidentifer.com",
          photoURL: ""
        });
      }
      setIsSignInOpen(false);
    }
  };

  const handleEmailSignIn = (email: string) => {
    setUser({
      uid: "email-" + btoa(email).substring(0, 10),
      name: email.split("@")[0].charAt(0).toUpperCase() + email.split("@")[0].slice(1),
      email: email,
      photoURL: ""
    });
    setIsSignInOpen(false);
  };

  const handleSignOut = async () => {
    if (isRealFirebaseEnabled) {
      try {
        await logoutFromGoogle();
        setIsDashboardOpen(false);
      } catch (err) {
        console.error("Firebase sign-out failed:", err);
      }
    } else {
      setUser(null);
      setIsDashboardOpen(false);
    }
  };

  // Lazy load and run heavy blog contents auto-updater on behalf of bishalkhanal7021@gmail.com
  useEffect(() => {
    if (
      user &&
      user.email === "bishalkhanal7021@gmail.com" &&
      !user.uid.startsWith("simulated-") &&
      !user.uid.startsWith("mock-") &&
      !user.uid.startsWith("email-") &&
      isRealFirebaseEnabled &&
      db &&
      !sessionStorage.getItem("blog_updater_session_completed")
    ) {
      sessionStorage.setItem("blog_updater_session_completed", "true");
      import("./utils/blogUpdater")
        .then(({ runBlogUpdater }) => {
          runBlogUpdater(user, db);
        })
        .catch(() => {});
    }
  }, [user, db]);

  const scrollToScanner = () => {
    if (route !== "/identify") {
      navigate("/identify");
    } else {
      const el = document.getElementById("interactive-drop-zone");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const testimonials = [
    {
      id: "t-1",
      name: "Sarah Johnson",
      role: "Garden Enthusiast",
      stars: 5,
      avatar: "",
      text: "This app is incredible! It identifies plants so accurately and gives amazing details on sunlight and watering schedules. It has instantly saved three of my ferns."
    },
    {
      id: "t-2",
      name: "Mike Chen",
      role: "Nature Photographer",
      stars: 5,
      avatar: "",
      text: "I've learned so much about the wild plants around my hiking trails. Excellent botanical summaries with great confidence ratings. Definitely a must-have app!"
    },
    {
      id: "t-3",
      name: "Emma Davis",
      role: "Plant Parent",
      stars: 5,
      avatar: "",
      text: "Perfect for beginners like me. Helped me identify so many plants in my new garden! The health diagnostic assessment is beautifully designed."
    }
  ];

  const isDashboardRoute = route.startsWith("/dashboard") || route === "/delete-account" || route === "/account-delete" || isDashboardOpen;

  if (isDashboardRoute && user) {
    const activeTabToRender = getTabFromRoute(route);
    return (
      <>
        <RouteProgressBar active={isNavigating} />
        <ScrollProgress />
        <Suspense fallback={<LazyFallback />}>
          <Dashboard 
            user={user} 
            onSignOut={() => {
              handleSignOut();
              setIsDashboardOpen(false);
              navigate("/");
            }} 
            onClose={() => {
              setIsDashboardOpen(false);
              navigate("/");
            }} 
            onIdentifyClick={() => {
              setIsDashboardOpen(false);
              setTimeout(() => {
                navigate("/identify");
              }, 300);
            }}
            onPlantClick={(plantSlug) => {
              setIsDashboardOpen(false);
              navigate(`/plant/${plantSlug}`);
            }}
            defaultTab={activeTabToRender}
            navigate={navigate}
          />
        </Suspense>
      </>
    );
  }

  if (route.startsWith("/blog/") && !route.startsWith("/blog/page/")) {
    const slug = route.substring("/blog/".length);
    return (
      <div className="min-h-screen bg-white font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
        <RouteProgressBar active={isNavigating} />
        <ScrollProgress />
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <main id="main-content">
          <Suspense fallback={<LazyFallback />}>
            <BlogArticlePage 
              slug={slug}
              onHomeClick={() => navigate("/")}
              onBlogClick={(targetSlug) => navigate(`/blog/${targetSlug}`)}
              onPlantClick={(targetSlug) => navigate(`/plant/${targetSlug}`)}
              navigate={navigate}
            />
          </Suspense>
        </main>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />

        <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />

        {/* MODAL 2: ANDROID MOCK DOWNLOAD APP DIALOG */}
        <AnimatePresence>
          {isGetAppOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsGetAppOpen(false)}
                className="absolute inset-0 bg-black/65 backdrop-blur-xs"
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full mx-auto shadow-2xl border border-gray-100 text-center space-y-6"
              >
                <button
                  onClick={() => setIsGetAppOpen(false)}
                  className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer animate-none"
                  aria-label="Close Get App Modal"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="w-12 h-12 rounded-xl bg-green-50 mx-auto flex items-center justify-center text-green-600">
                  <Smartphone className="w-6 h-6 text-green-500" />
                </div>

                <div>
                  <h4 className="text-2xl font-extrabold text-gray-950 tracking-tight">
                    Get the PlantID AI App
                  </h4>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Enjoy instantaneous species recognition with scanning history. Available on any Android device!
                  </p>
                </div>

                {/* QR Code Graphic Placeholder */}
                <div className="max-w-[150px] mx-auto bg-gray-50 p-3 rounded-2xl border border-gray-150 flex flex-col items-center gap-1">
                  <div className="w-28 h-28 bg-gradient-to-tr from-green-600 to-emerald-400 rounded-lg flex items-center justify-center relative">
                    <div className="absolute inset-1.5 bg-white rounded flex items-center justify-center p-1">
                      <div className="w-full h-full bg-gray-900 rounded-xs grid grid-cols-4 gap-1 p-1 opacity-90">
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                      </div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-gray-405 tracking-wider uppercase mt-1">Scan to Download</span>
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    onClick={() => {
                      alert("Mock APK download started! In a production setting, this pulls our native app directly.");
                      setIsGetAppOpen(false);
                    }}
                    className="w-full py-3.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-xl font-bold text-sm shadow-md cursor-pointer select-none"
                  >
                    Download Direct APK (V1.24)
                  </button>
                </div>

              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const isStaticRoute = [
    "/about",
    "/contact",
    "/privacy-policy",
    "/terms-of-service",
    "/disclaimer",
    "/cookie-policy",
    "/faq",
    "/blog",
    "/plant-care-guides",
    "/plant-diseases",
    "/common-plant-diseases",
    "/botanical-articles",
    "/plant-care-tips",
    "/gardening-guides",
    "/data-sources",
    "/editorial-policy",
    "/attribution"
  ].includes(route) || 
  route.startsWith("/botanical-articles/") || 
  route.startsWith("/blog/page/");

  if (isStaticRoute) {
    return (
      <div className="min-h-screen bg-white font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
        <RouteProgressBar active={isNavigating} />
        <ScrollProgress />
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <main id="main-content">
          <Suspense fallback={<LazyFallback />}>
            <StaticPage route={route} navigate={navigate} />
          </Suspense>
        </main>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />

        <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />
      </div>
    );
  }

  const isToolRoute = 
    route.startsWith("/tools/") || 
    route === "/tools" || 
    route === "/tools/" ||
    [
      "/disease-identifier",
      "/fertilization-planner",
      "/flower-identifier",
      "/tree-identifier",
      "/weed-identifier",
      "/watering-calculator",
      "/soil-ph-analyzer",
      "/sunlight-exposure-meter"
    ].includes(route);

  if (isToolRoute) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
        <RouteProgressBar active={isNavigating} />
        <ScrollProgress />
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <main id="main-content">
          <Suspense fallback={<LazyFallback />}>
            {route === "/tools" || route === "/tools/" ? (
              <ToolsShowcase navigate={navigate} tools={toolsList} />
            ) : (
              <ToolPage 
                route={route} 
                tools={toolsList} 
                navigate={navigate} 
                onHomeClick={() => navigate("/")} 
              />
            )}
          </Suspense>
        </main>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />

        <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />
      </div>
    );
  }

  if (route.startsWith("/plant/")) {
    const slug = route.substring("/plant/".length);
    return (
      <div className="min-h-screen bg-white font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
        <RouteProgressBar active={isNavigating} />
        <ScrollProgress />
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <main id="main-content">
          <Suspense fallback={<LazyFallback />}>
            <PlantDetailsPage 
              slug={slug}
              user={user}
              onSignInClick={() => setIsSignInOpen(true)}
              onHomeClick={() => navigate("/")}
              onPlantClick={(targetSlug) => navigate(`/plant/${targetSlug}`)}
            />
          </Suspense>
        </main>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />

        <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />

        {/* MODAL 2: ANDROID MOCK DOWNLOAD APP DIALOG */}
        <AnimatePresence>
          {isGetAppOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsGetAppOpen(false)}
                className="absolute inset-0 bg-black/65 backdrop-blur-xs"
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full mx-auto shadow-2xl border border-gray-100 text-center space-y-6"
              >
                <button
                  onClick={() => setIsGetAppOpen(false)}
                  className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer animate-none"
                  aria-label="Close Get App Modal"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="w-12 h-12 rounded-xl bg-green-50 mx-auto flex items-center justify-center text-green-600">
                  <Smartphone className="w-6 h-6 text-green-500" />
                </div>

                <div>
                  <h4 className="text-2xl font-extrabold text-gray-950 tracking-tight">
                    Get the PlantID AI App
                  </h4>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Enjoy instantaneous species recognition with scanning history. Available on any Android device!
                  </p>
                </div>

                {/* QR Code Graphic Placeholder */}
                <div className="max-w-[150px] mx-auto bg-gray-50 p-3 rounded-2xl border border-gray-150 flex flex-col items-center gap-1">
                  <div className="w-28 h-28 bg-gradient-to-tr from-green-600 to-emerald-400 rounded-lg flex items-center justify-center relative">
                    <div className="absolute inset-1.5 bg-white rounded flex items-center justify-center p-1">
                      <div className="w-full h-full bg-gray-900 rounded-xs grid grid-cols-4 gap-1 p-1 opacity-90">
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                      </div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-gray-405 tracking-wider uppercase mt-1">Scan to Download</span>
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    onClick={() => {
                      alert("Mock APK download started! In a production setting, this pulls our native app directly.");
                      setIsGetAppOpen(false);
                    }}
                    className="w-full py-3.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-xl font-bold text-sm shadow-md cursor-pointer select-none"
                  >
                    Download Direct APK (V1.24)
                  </button>
                </div>

              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (route === "/admin" || route.startsWith("/admin")) {
    return (
      <div className="min-h-screen bg-[#111612] font-sans selection:bg-emerald-150 selection:text-emerald-900 scroll-smooth antialiased">
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <Suspense fallback={<AdminLazyFallback />}>
          <AdminPage 
            user={user}
            onSignInClick={handleSignIn}
            onHomeClick={() => navigate("/")}
            isAdmin={isAdmin}
            onSignOut={handleSignOut}
          />
        </Suspense>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />
      </div>
    );
  }

  const isDatabasePage = route.startsWith("/database") || route.startsWith("/plant-database") || route.startsWith("/plants/");
  if (isDatabasePage) {
    let initialCategory = "All";
    let initialQuery = "";
    if (route.startsWith("/plants/")) {
      const catSlug = route.substring("/plants/".length).toLowerCase().split("?")[0];
      if (catSlug === "indoor") {
        initialCategory = "Indoor";
      } else if (catSlug === "flowering") {
        initialCategory = "Flowering";
      } else if (catSlug === "succulents") {
        initialCategory = "Succulents";
      } else if (catSlug === "medicinal" || catSlug === "medicinal-plants") {
        initialCategory = "Medicinal";
      } else if (catSlug === "herbs") {
        initialCategory = "Herbs";
      } else if (catSlug === "trees") {
        initialCategory = "Trees";
      } else if (catSlug === "shrubs") {
        initialCategory = "Outdoor";
        initialQuery = "Shrub";
      } else if (catSlug === "tropical" || catSlug === "tropical-plants") {
        initialCategory = "All";
        initialQuery = "Tropical";
      }
    }

    return (
      <div className="min-h-screen bg-white font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <main id="main-content">
          <Suspense fallback={<LazyFallback />}>
            <DatabasePage 
              user={user}
              onSignInClick={() => setIsSignInOpen(true)}
              onHomeClick={() => navigate("/")}
              onPlantClick={(targetSlug) => navigate(`/plant/${targetSlug}`)}
              navigate={navigate}
              route={route}
              initialCategory={initialCategory}
              initialQuery={initialQuery}
            />
          </Suspense>
        </main>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />

        <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />

        {/* MODAL 2: ANDROID MOCK DOWNLOAD APP DIALOG */}
        <AnimatePresence>
          {isGetAppOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsGetAppOpen(false)}
                className="absolute inset-0 bg-black/65 backdrop-blur-xs"
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full mx-auto shadow-2xl border border-gray-100 text-center space-y-6"
              >
                <button
                  onClick={() => setIsGetAppOpen(false)}
                  className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer animate-none"
                  aria-label="Close Get App Modal"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="w-12 h-12 rounded-xl bg-green-50 mx-auto flex items-center justify-center text-green-600">
                  <Smartphone className="w-6 h-6 text-green-500" />
                </div>

                <div>
                  <h3 className="text-2xl font-extrabold text-gray-950 tracking-tight">
                    Get the PlantID AI App
                  </h3>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Enjoy instantaneous species recognition with scanning history. Available on any Android device!
                  </p>
                </div>

                {/* QR Code Graphic Placeholder */}
                <div className="max-w-[150px] mx-auto bg-gray-50 p-3 rounded-2xl border border-gray-150 flex flex-col items-center gap-1">
                  <div className="w-28 h-28 bg-gradient-to-tr from-green-600 to-emerald-400 rounded-lg flex items-center justify-center relative">
                    <div className="absolute inset-1.5 bg-white rounded flex items-center justify-center p-1">
                      <div className="w-full h-full bg-gray-900 rounded-xs grid grid-cols-4 gap-1 p-1 opacity-90">
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                      </div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-gray-455 tracking-wider uppercase mt-1">Scan to Download</span>
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    onClick={() => {
                      alert("Mock APK download started! In a production setting, this pulls our native app directly.");
                      setIsGetAppOpen(false);
                    }}
                    className="w-full py-3.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-xl font-bold text-sm shadow-md cursor-pointer select-none"
                  >
                    Download Direct APK (V1.24)
                  </button>
                </div>

              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (route === "/account-delete" || route === "/delete-account") {
    return (
      <div className="min-h-screen bg-white font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <main id="main-content">
          <Suspense fallback={<LazyFallback />}>
            <AccountDeletePage 
              user={user}
              onSignInClick={() => setIsSignInOpen(true)}
              onHomeClick={() => navigate("/")}
              onSignOut={handleSignOut}
            />
          </Suspense>
        </main>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />

        <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />
      </div>
    );
  }

  if (route === "/identify" || route === "/identify-plant") {
    return (
      <div className="min-h-screen bg-white font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
        <Header 
          onIdentifyClick={scrollToScanner} 
          onGetAppClick={() => setIsGetAppOpen(true)}
          user={user}
          onSignInClick={() => setIsSignInOpen(true)}
          onSignOut={handleSignOut}
          onDashboardClick={handleDashboardTabClick}
          navigate={navigate}
          tools={toolsList}
        />
        <main id="main-content">
          <Suspense fallback={<LazyFallback />}>
            <IdentifyPage 
              user={user}
              onSignInClick={() => setIsSignInOpen(true)}
              onHomeClick={() => navigate("/")}
              onDashboardClick={handleDashboardTabClick}
            />
          </Suspense>
        </main>
        <Footer setIsGetAppOpen={setIsGetAppOpen} />

        <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />

        {/* MODAL 2: ANDROID MOCK DOWNLOAD APP DIALOG */}
        <AnimatePresence>
          {isGetAppOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsGetAppOpen(false)}
                className="absolute inset-0 bg-black/65 backdrop-blur-xs"
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full mx-auto shadow-2xl border border-gray-100 text-center space-y-6"
              >
                <button
                  onClick={() => setIsGetAppOpen(false)}
                  className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer animate-none"
                  aria-label="Close Get App Modal"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="w-12 h-12 rounded-xl bg-green-50 mx-auto flex items-center justify-center text-green-600">
                  <Smartphone className="w-6 h-6 text-green-500" />
                </div>

                <div>
                  <h4 className="text-2xl font-extrabold text-gray-950 tracking-tight">
                    Get the PlantID AI App
                  </h4>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Enjoy instantaneous species recognition with scanning history. Available on any Android device!
                  </p>
                </div>

                {/* QR Code Graphic Placeholder */}
                <div className="max-w-[150px] mx-auto bg-gray-50 p-3 rounded-2xl border border-gray-150 flex flex-col items-center gap-1">
                  <div className="w-28 h-28 bg-gradient-to-tr from-green-600 to-emerald-400 rounded-lg flex items-center justify-center relative">
                    <div className="absolute inset-1.5 bg-white rounded flex items-center justify-center p-1">
                      <div className="w-full h-full bg-gray-900 rounded-xs grid grid-cols-4 gap-1 p-1 opacity-90">
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                        <div className="bg-white rounded-xs"></div>
                        <div className="bg-transparent"></div>
                      </div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-gray-650 tracking-wider uppercase mt-1">Scan to Download</span>
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    onClick={() => {
                      alert("Mock APK download started! In a production setting, this pulls our native app directly.");
                      setIsGetAppOpen(false);
                    }}
                    className="w-full py-3.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-xl font-bold text-sm shadow-md cursor-pointer select-none"
                  >
                    Download Direct APK (V1.24)
                  </button>
                </div>

              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans text-gray-950 selection:bg-green-150 selection:text-green-900 scroll-smooth antialiased">
      <RouteProgressBar active={isNavigating} />
      <ScrollProgress />
      
      {/* 1. Header & Social Proof row */}
      <Header 
        onIdentifyClick={scrollToScanner} 
        onGetAppClick={() => setIsGetAppOpen(true)}
        user={user}
        onSignInClick={() => setIsSignInOpen(true)}
        onSignOut={handleSignOut}
        onDashboardClick={handleDashboardTabClick}
        navigate={navigate}
        tools={toolsList}
      />

      <main id="main-content">
        {/* 2. Hero Section */}
      <section id="home" className="pt-20 pb-20 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-[50%] h-[100%] bg-gradient-to-l from-green-500/5 to-transparent pointer-events-none -z-10" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Dynamic Premium Factual Trust Ribbon Bar */}
          <div className="flex flex-col items-center justify-center mb-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTrustVariant}
                initial={{ opacity: 0, y: -6, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.99 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                whileHover={{ scale: 1.012 }}
                className="inline-flex max-w-full items-center justify-center bg-green-50/50 backdrop-blur-md border border-green-250 py-2.5 px-6 sm:px-8 rounded-full shadow-xs hover:shadow-md cursor-pointer transition-all duration-300 select-none hover:border-green-300"
                onClick={() => {
                  const cycleMap: Record<string, "A" | "B" | "C" | "D"> = { A: "B", B: "C", C: "D", D: "A" };
                  setActiveTrustVariant(cycleMap[activeTrustVariant]);
                }}
                title="Click to cycle features"
              >
                {activeTrustVariant === "A" && (
                  <div className="flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 text-xs sm:text-sm font-semibold text-green-900 tracking-tight">
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <Leaf className="w-4 h-4 text-green-600 shrink-0" />
                      <span>AI Plant Identification</span>
                    </span>
                    <span className="text-green-300 select-none hidden sm:inline">•</span>
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <Database className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>Species Database</span>
                    </span>
                    <span className="text-green-300 select-none hidden sm:inline">•</span>
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <BookOpen className="w-4 h-4 text-teal-600 shrink-0" />
                      <span>Care Guides</span>
                    </span>
                  </div>
                )}

                {activeTrustVariant === "B" && (
                  <div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-green-900 tracking-tight">
                    <Search className="w-4 h-4 text-green-600 shrink-0" />
                    <span>Identify Plants, Flowers, Trees & Diseases with AI</span>
                  </div>
                )}

                {activeTrustVariant === "C" && (
                  <div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-green-900 tracking-tight">
                    <Brain className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Powered by Advanced Plant Computer Vision</span>
                  </div>
                )}

                {activeTrustVariant === "D" && (
                  <div className="flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 text-xs sm:text-sm font-semibold text-green-900 tracking-tight">
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <Database className="w-4 h-4 text-green-600 shrink-0" />
                      <span>Plant Profiles</span>
                    </span>
                    <span className="text-green-300 select-none hidden sm:inline">•</span>
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <BookOpen className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>Care Guides</span>
                    </span>
                    <span className="text-green-300 select-none hidden sm:inline">•</span>
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <Sparkles className="w-4 h-4 text-teal-600 shrink-0" />
                      <span>Factual AI Analysis</span>
                    </span>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Hero Details */}
            <div className="lg:col-span-7 space-y-8 text-center lg:text-left">
              
              {/* 1. AI Badge */}
              <div id="hero-ai-badge" className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 rounded-full border border-green-200 text-green-700 text-xs font-bold uppercase tracking-wider shadow-xs shadow-green-100/20 select-none">
                <Leaf className="w-3.5 h-3.5" /> AI-Powered Recognition
              </div>

              {/* 2. Main Headline */}
              <h1 id="hero-headline" className="text-5xl sm:text-7xl font-[900] text-gray-950 tracking-tight leading-[1.08] flex flex-col gap-1 select-none">
                <span className="block">Identify Plants</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500 inline-flex items-center justify-center lg:justify-start gap-1">
                  With AI <span className="inline-block transition-transform duration-300 hover:rotate-12">🌿</span>
                </span>
              </h1>

              {/* 3. Description */}
              <p id="hero-description" className="text-lg text-gray-600 font-medium max-w-[550px] mx-auto lg:mx-0 leading-relaxed">
                Identify plants, flowers, trees, leaves, and plant diseases in seconds with professional computer vision diagnostics.
              </p>

              {/* 4. CTA Buttons */}
              <div id="hero-cta-buttons" className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <button
                  id="primary-cta-button"
                  onClick={scrollToScanner}
                  className="px-8 py-4 bg-green-800 hover:bg-green-900 text-white font-extrabold text-base rounded-2xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-250 w-full sm:w-auto flex items-center justify-center gap-3 cursor-pointer min-h-[56px]"
                >
                  <Camera className="w-5 h-5 stroke-2 animate-bounce" />
                  <span>Start AI Identification</span>
                </button>

                <button
                  id="secondary-cta-button"
                  onClick={() => {
                    const el = document.getElementById("database");
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth" });
                    } else {
                      navigate("/database");
                    }
                  }}
                  className="px-8 py-4 bg-white border border-gray-200 text-gray-850 font-extrabold text-base rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-250 w-full sm:w-auto flex items-center justify-center gap-3 cursor-pointer shadow-3xs hover:-translate-y-0.5 active:translate-y-0 min-h-[56px]"
                >
                  <Database className="w-5 h-5 stroke-2 text-green-600 animate-pulse" />
                  <span>Explore Plant Database</span>
                </button>
              </div>

              {/* Microcopy below buttons */}
              <div id="cta-microcopy" className="flex flex-wrap items-center justify-center lg:justify-start gap-x-2.5 gap-y-1.5 text-xs text-gray-600 font-medium pt-1">
                <span className="text-gray-650 font-bold uppercase tracking-wider text-[10px]">Supports:</span>
                <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-150/50">Plants</span>
                <span className="text-gray-300">•</span>
                <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-150/50">Flowers</span>
                <span className="text-gray-300">•</span>
                <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-150/50">Trees</span>
                <span className="text-gray-300">•</span>
                <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-150/50">Leaves</span>
                <span className="text-gray-300">•</span>
                <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-100/80 px-2 py-0.5 rounded-md font-semibold">Plant Diseases</span>
              </div>

              {/* Professional Believable Trust Bar */}
              <div id="trust-bar" className="flex items-center justify-center lg:justify-start gap-2.5 pt-6 border-t border-gray-150/75 max-w-[550px] mx-auto lg:mx-0">
                <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center shrink-0 border border-green-200">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div className="text-sm text-gray-600 font-semibold text-center sm:text-left leading-snug">
                  AI-Powered Plant Identification Platform
                </div>
              </div>

            </div>

            {/* Right Hero Visual elements - smartphone Mockup */}
            <div id="hero-phone-container" className="lg:col-span-5 relative flex justify-center items-center py-6">
              
              {/* Green gradient glow background */}
              <div className="absolute w-[380px] h-[380px] bg-gradient-to-tr from-green-400/10 via-emerald-400/5 to-transparent rounded-full blur-3xl pointer-events-none -z-10" />
              <div className="absolute w-64 h-64 bg-green-500/5 -bottom-10 -right-5 rounded-full blur-[80px] pointer-events-none -z-10" />

              {/* Subtle botanical / SaaS dotted grid backdrop illustration instead of random leaves */}
              <div className="absolute inset-0 border border-dashed border-gray-200/40 rounded-[48px] pointer-events-none -z-10" />
              <div className="absolute -top-10 -bottom-10 -left-12 -right-12 bg-[radial-gradient(#e5e7eb_1.2px,transparent_1.2px)] [background-size:20px_20px] opacity-30 mix-blend-multiply pointer-events-none -z-20" />
              
              <div className="absolute -left-16 top-16 text-gray-300/40 pointer-events-none -z-10 select-none">
                <svg width="120" height="120" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M50,90 C50,90 20,60 20,40 C20,20 50,10 50,10 C50,10 80,20 80,40 C80,60 50,90 50,90 Z" />
                  <path d="M50,10 L50,90" strokeDasharray="3,3" />
                  <path d="M50,30 L30,45" />
                  <path d="M50,45 L70,30" />
                  <path d="M50,55 L25,65" />
                  <path d="M50,70 L75,55" />
                </svg>
              </div>

              <div className="absolute -right-16 bottom-20 text-gray-350/30 pointer-events-none -z-10 select-none">
                <svg width="100" height="100" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M50,90 C50,90 20,60 20,40 C20,20 50,10 50,10 C50,10 80,20 80,40 C80,60 50,90 50,90 Z" />
                  <path d="M50,10 L50,90" strokeDasharray="3,3" />
                  <path d="M50,35 L35,48" strokeWidth="0.75" />
                  <path d="M50,50 L65,37" strokeWidth="0.75" strokeDasharray="2,1" />
                </svg>
              </div>

              {/* Floating Blurred Glass Ball Backdrop Accent */}
              <div className="absolute right-[-40px] top-10 w-24 h-24 rounded-full bg-white/10 backdrop-blur-md border border-white/20 shadow-xs -z-5 pointer-events-none animate-float-gentle" />

              {/* Large Floating Smartphone Mockup with premium Realistic appearance */}
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative w-full max-w-[320px] aspect-[9/18.5] bg-slate-950 rounded-[48px] shadow-[0_25px_60px_-15px_rgba(22,163,74,0.18),0_0_0_1px_rgba(255,255,255,0.12)] p-3 border-[6px] border-slate-900/90 select-none z-10 animate-float-gentle"
              >
                
                {/* Sleek speaker notch & dynamic island */}
                <div className="absolute top-5 left-1/2 -translate-x-1/2 w-28 h-5 bg-slate-950 rounded-full z-30 flex items-center justify-between px-4">
                  <div className="w-1.5 h-1.5 bg-[#0f172a] rounded-full border border-slate-800/40" />
                  <div className="w-10 h-0.5 bg-slate-800/80 rounded-full" />
                </div>

                {/* Internal App Screen */}
                <div className="h-full w-full bg-[#11231a] rounded-[36px] overflow-hidden flex flex-col relative border border-white/10">
                  
                  {/* Fill-to-edge Real Plant Scanning Viewport */}
                  <div className="absolute inset-0 w-full h-full bg-[#030704] flex flex-col items-center justify-center select-none overflow-hidden">
                    
                    {/* Background Tech Grid & Radials */}
                    <div className="absolute inset-0 opacity-15 mix-blend-screen" style={{
                      backgroundImage: `radial-gradient(#10b981 1.2px, transparent 1.2px), linear-gradient(rgba(16,185,129,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.06) 1px, transparent 1px)`,
                      backgroundSize: "20px 20px, 10px 10px, 10px 10px"
                    }} />

                    {/* Ambient Neon Backlight Behind Plant */}
                    <div className="absolute w-56 h-56 bg-emerald-500/20 rounded-full blur-[40px] pointer-events-none z-0 animate-pulse-glow" />

                    {/* Background blurred filler image for organic depth */}
                    <img 
                      src="/assets/plant.png"
                      alt="AI scan ambient texture"
                      width={413}
                      height={620}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover object-center scale-[1.15] filter blur-lg opacity-25 brightness-[0.35] pointer-events-none z-0"
                    />

                    {/* Primary Specimen Image */}
                    <div className="absolute inset-0 w-full h-full flex items-center justify-center p-3 z-10">
                      <img 
                        src="/assets/plant.png"
                        alt="AI plant detector specimen scanning"
                        width={413}
                        height={620}
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        className="w-[88%] h-[68%] object-contain object-center drop-shadow-[0_0_35px_rgba(16,185,129,0.6)] saturate-[1.15] contrast-[1.08] animate-float-gentle"
                      />
                    </div>

                    {/* Outer Radial vignette & grid filter to blend with futuristic UI */}
                    <div 
                      className="absolute inset-0 pointer-events-none z-12 mix-blend-multiply" 
                      style={{
                        background: "radial-gradient(circle, transparent 35%, rgba(10,21,15,0.92) 100%)"
                      }}
                    />

                    {/* Dynamic Specimen Label Tag */}
                    <div className="absolute bottom-[235px] left-1/2 -translate-x-1/2 z-25 bg-[#0b1c12]/90 backdrop-blur-md border border-emerald-550/40 rounded-full px-3 py-1 flex items-center gap-1.5 shadow-lg shadow-emerald-950/30 select-none">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(52,211,153,1)]" />
                      <span className="text-[7.5px] uppercase tracking-[0.16em] text-emerald-300 font-mono font-black whitespace-nowrap">
                        SPECIMEN: MONSTERA DELICIOSA
                      </span>
                    </div>

                  </div>

                  {/* Top Status Indicators inside mockup */}
                  <div className="flex items-center justify-between text-[11px] font-bold text-white/80 absolute top-7 left-6 right-6 z-20">
                    <span className="tracking-tight">9:41</span>
                    <div className="flex gap-1 items-center">
                      <div className="w-5 h-2.5 border border-white/60 rounded-[4px] p-0.5 flex items-center">
                        <div className="bg-white h-full w-3.5 rounded-[2px]" />
                      </div>
                    </div>
                  </div>

                  {/* AI SCAN OVERLAY */}
                  {/* 1. Scanning Frame corners (enhanced with emerald hue & dropshadow GLOW) */}
                  <div className="absolute top-20 left-6 w-5 h-5 border-t-2 border-l-2 border-emerald-400 rounded-tl-[6px] z-25 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                  <div className="absolute top-20 right-6 w-5 h-5 border-t-2 border-r-2 border-emerald-400 rounded-tr-[6px] z-25 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                  <div className="absolute bottom-48 left-6 w-5 h-5 border-b-2 border-l-2 border-emerald-400 rounded-bl-[6px] z-25 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                  <div className="absolute bottom-48 right-6 w-5 h-5 border-b-2 border-r-2 border-emerald-400 rounded-br-[6px] z-25 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]" />

                  {/* 2. Scanning Laser Animation Beam */}
                  <div 
                    className="absolute left-6 right-6 h-[2.5px] bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_15px_4px_rgba(16,185,129,0.75),0_0_4px_1px_rgba(255,255,255,0.9)] z-25 pointer-events-none animate-laser-beam"
                  />

                  {/* 3. AI Target Indicator with green pulse & rotating dash ring */}
                  <div className="absolute top-[36%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center">
                    
                    {/* Ring Pulse Backdrop */}
                    <div 
                      className="absolute w-24 h-24 bg-emerald-500/25 rounded-full blur-md animate-pulse-glow"
                    />

                    {/* Rotating Dash Outer Reticle Vector */}
                    <div 
                      className="absolute w-16 h-16 border-2 border-dashed border-emerald-400 rounded-full opacity-80 animate-spin-slow"
                    />

                    {/* Inner Circular Target Emblem with Leaf/Sparkles icon */}
                    <div className="w-11 h-11 bg-gradient-to-tr from-emerald-500 to-teal-400 rounded-full flex items-center justify-center text-white shadow-[0_0_15px_rgba(16,185,129,0.75)] border border-white/20">
                      <Sparkles className="w-5 h-5 animate-pulse" />
                    </div>
                  </div>

                  {/* RESULT CARD: Floating Glass Card at bottom of screen */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="absolute bottom-5 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-white/40 shadow-[0_15px_30px_rgba(0,0,0,0.12),0_0_2px_rgba(0,0,0,0.04)] z-30"
                  >
                    
                    {/* Species and confidences rows */}
                    <div className="flex justify-between items-start">
                      <div className="space-y-0.5">
                        <span className="text-gray-900 font-[900] text-sm tracking-tight block">
                          Monstera Deliciosa
                        </span>
                        <span className="text-[10px] text-gray-650 font-bold block">
                          Swiss Cheese Plant
                        </span>
                      </div>
                      <div className="shrink-0 bg-green-50 text-green-700 font-extrabold text-[10px] px-2 py-0.5 rounded-md border border-green-100/50">
                        95% Match
                      </div>
                    </div>

                    {/* Thin progress track indicator bar */}
                    <div className="mt-3.5 space-y-1">
                      <div className="flex justify-between items-center text-[9px] text-gray-650 font-bold uppercase tracking-wider">
                        <span>Analysis rate</span>
                        <span className="text-green-600 font-extrabold">Excellent</span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: "95%" }}
                          transition={{ delay: 0.7, duration: 1.2, ease: "easeOut" }}
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
                        />
                      </div>
                    </div>

                    {/* Action trigger View details link */}
                    <button
                      onClick={() => navigate("/plant/monstera-deliciosa")}
                      className="w-full mt-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-1 cursor-pointer"
                    >
                      View Details <span>→</span>
                    </button>

                  </motion.div>

                </div>
              </motion.div>

            </div>

          </div>

          {/* Trust Section Below Hero: Feature Badges */}
          <div id="hero-features-bar" className="mt-16 pt-8 border-t border-gray-150/70 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { label: "Plant Recognition", desc: "Automated photo mapping", icon: <Camera className="w-5 h-5 text-green-600" /> },
              { label: "Species Database", desc: "Botanical taxonomy data", icon: <BookOpen className="w-5 h-5 text-emerald-600" /> },
              { label: "Care Guides", desc: "Expert cultivation stats", icon: <Leaf className="w-5 h-5 text-teal-600" /> },
              { label: "Disease Detection", desc: "Scan foliage diagnostics", icon: <Sparkles className="w-5 h-5 text-rose-600" /> },
              { label: "AI Analysis", desc: "Factual neural analytics", icon: <Globe className="w-5 h-5 text-indigo-600" /> }
            ].map((feat, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-3 p-3.5 bg-white/50 backdrop-blur-xs hover:bg-green-50/10 border border-gray-150/50 hover:border-green-150/80 rounded-xl transition-all shadow-3xs"
              >
                <div className="shrink-0 p-2 bg-gray-50/80 rounded-lg">
                  {feat.icon}
                </div>
                <div className="text-left select-none">
                  <span className="block font-bold text-xs text-gray-900 tracking-tight leading-snug">
                    {feat.label}
                  </span>
                  <span className="block text-[10px] text-gray-400 mt-0.5 font-medium leading-none">
                    {feat.desc}
                  </span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

        {!user && (
          <section id="account-benefits-section" className="py-12 max-w-5xl mx-auto px-4 sm:px-6">
            <AccountBenefits onSignInClick={() => setIsSignInOpen(true)} />
          </section>
        )}

        {/* 2B. Tools Showcase */}
        <ToolsShowcase navigate={navigate} tools={toolsList} />

        {/* 3. Features Bento Grid */}
        <BentoGrid />

        {/* 4. Scanner Space */}
        <Scanner scannerRef={scannerRef} />

        {/* 5. How It Works Section */}
        <HowItWorks onScanClick={scrollToScanner} />

        {/* 5B. Educational Content Grid (Sticky Table of Contents & Pillars) */}
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 xl:grid-cols-12 gap-10">
          
          {/* Left Sticky Sidebar: Table of Contents & Targeted Keywords */}
          <aside className="hidden xl:block xl:col-span-3 h-fit sticky top-24 self-start space-y-4 max-h-[calc(100vh-110px)] overflow-y-auto pr-1">
            <TableOfContents 
              title="Homepage Navigation"
              items={[
                { id: "introduction", label: "1. Introduction" },
                { id: "what-is-ai", label: "2. What is AI Plant ID?" },
                { id: "how-to-identify", label: "3. How to Identify" },
                { id: "why-choose-us", label: "4. Why Choose Us?" },
                { id: "benefits", label: "5. Benefits Comparison" },
                { id: "tips-mistakes", label: "6. Tips & Mistakes" },
                { id: "categories", label: "7. Plant Categories" },
                { id: "database", label: "8. Botanical Database" },
                { id: "faq", label: "9. Frequently Asked Questions" },
                { id: "learn-hub", label: "10. Latest Articles" },
                { id: "about-us", label: "11. About Us" },
              ]} 
            />

            {/* Homepage Targeted Primary Keywords Box */}
            <HomepageTargetedKeywordsBox />
          </aside>

          {/* Right Main Column: Deep Educational Pillars & Sections */}
          <div className="xl:col-span-9 space-y-24">
            
            {/* Deep Core Pillars */}
            <HomepagePillars navigate={navigate} />

            {/* Plant Categories Section */}
            <PlantCategories />

            {/* Plant Learning Center Section */}
            <PlantLearningCenter />

            {/* Trust & Statistics Component */}
            <TrustStats />

            {/* Plant Database Showcase Section */}
            <PlantDatabase onPlantClick={(targetSlug) => navigate(`/plant/${targetSlug}`)} />

            {/* Frequently Asked Questions */}
            <FAQ />

            {/* Plant Learning Hub & Blog Section */}
            <BlogSection navigate={navigate} />

            {/* About The Project Section */}
            <AboutTheProject />

          </div>
        </div>

        {/* 6C. Legal & Compliance Hub & Contacts */}
        <LegalHub />
      </main>

      {/* 7. Footer Section */}
      <Footer setIsGetAppOpen={setIsGetAppOpen} />

      {/* MODAL 1: PRE-INTEGRATED AUTHENTICATION SIGN IN MODAL */}
      <SignInModal isOpen={isSignInOpen} onClose={() => setIsSignInOpen(false)} onGoogleSignIn={handleSignIn} onEmailSignIn={handleEmailSignIn} />

      {/* MODAL 2: ANDROID MOCK DOWNLOAD APP DIALOG */}
      <AnimatePresence>
        {isGetAppOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsGetAppOpen(false)}
              className="absolute inset-0 bg-black/65 backdrop-blur-xs"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full mx-auto shadow-2xl border border-gray-100 text-center space-y-6"
            >
              <button
                onClick={() => setIsGetAppOpen(false)}
                className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer animate-none"
                aria-label="Close Get App Modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="w-12 h-12 rounded-xl bg-green-50 mx-auto flex items-center justify-center text-green-600">
                <Smartphone className="w-6 h-6 text-green-500" />
              </div>

              <div>
                <h4 className="text-2xl font-extrabold text-gray-950 tracking-tight">
                  Get the PlantID AI App
                </h4>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  Enjoy instantaneous species recognition with scanning history. Available on any Android device!
                </p>
              </div>

              {/* QR Code Graphic Placeholder */}
              <div className="max-w-[150px] mx-auto bg-gray-50 p-3 rounded-2xl border border-gray-150 flex flex-col items-center gap-1">
                {/* Simulated high-quality custom QR Box */}
                <div className="w-28 h-28 bg-gradient-to-tr from-green-600 to-emerald-400 rounded-lg flex items-center justify-center relative">
                  <div className="absolute inset-1.5 bg-white rounded flex items-center justify-center p-1">
                    {/* Retro Grid style QR matrix simulation */}
                    <div className="w-full h-full bg-gray-900 rounded-xs grid grid-cols-4 gap-1 p-1 opacity-90">
                      <div className="bg-white rounded-xs"></div>
                      <div className="bg-transparent"></div>
                      <div className="bg-white rounded-xs"></div>
                      <div className="bg-white rounded-xs"></div>
                      <div className="bg-transparent"></div>
                      <div className="bg-white rounded-xs"></div>
                      <div className="bg-transparent"></div>
                      <div className="bg-white rounded-xs"></div>
                      <div className="bg-white rounded-xs"></div>
                      <div className="bg-transparent"></div>
                      <div className="bg-white rounded-xs"></div>
                      <div className="bg-transparent"></div>
                    </div>
                  </div>
                </div>
                <span className="text-[9px] font-bold text-gray-650 tracking-wider uppercase mt-1">Scan to Download</span>
              </div>

              <div className="space-y-2 pt-2">
                <button
                  onClick={() => {
                    // Trigger a fast simulated direct apk file download URL as helper
                    alert("Mock APK download started! In a production setting, this pulls our native app directly.");
                    setIsGetAppOpen(false);
                  }}
                  className="w-full py-3.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-xl font-bold text-sm shadow-md cursor-pointer select-none"
                >
                  Download Direct APK (V1.24)
                </button>
                <div className="text-xs text-gray-450 font-bold uppercase tracking-wider block">
                  or
                </div>
                <span className="text-xs text-gray-500 font-semibold block leading-tight">
                  Search <strong>PlantID AI</strong> inside the <strong>Google Play Store</strong>.
                </span>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Admin Role Invitation Notification Banner */}
      {adminInvitation && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm w-full bg-emerald-950/95 border border-emerald-500/40 rounded-3xl p-6 shadow-2xl text-white backdrop-blur-md">
          <div className="flex gap-4">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center border border-emerald-500/35 text-emerald-400 shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h5 className="font-extrabold text-sm text-white tracking-wide">
                👑 Administrative Invitation
              </h5>
              <p className="text-xs text-emerald-100 font-medium leading-relaxed mt-1">
                You have been invited by <span className="text-emerald-300 font-bold font-mono">{adminInvitation.grantedBy}</span> to join the Admin Panel team as an <span className="bg-emerald-500/20 px-1.5 py-0.5 rounded text-[10px] uppercase font-black text-emerald-300 ml-1">{adminInvitation.role}</span>.
              </p>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={acceptAdminInvitation}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-500 hover:to-green-400 text-white rounded-xl text-xs font-black tracking-wide shadow-md cursor-pointer transition-all flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Accept Role</span>
                </button>
                <button
                  onClick={declineAdminInvitation}
                  className="px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl text-xs font-bold cursor-pointer transition-all"
                >
                  Decline
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
